<template>
  <Modal
    v-if="organisationAndMembers"
    class="organisation-group-add-dialog text-left"
    v-model="shown"
    :title="dialogTitle"
  >
    <div style="width: 100%">
      <div class="row">
        <div class="col-sm-12">
          <div class="col-sm-12">
            <p>Add {{ typeName }}s which do not belong to an existing organisation.</p>
            <p>
              <span class="text-warning">Note: </span>Any members of the {{ typeName }}s without an
              organisation will also be added to <b>{{ organisationAndMembers.name }}</b
              >.
            </p>
          </div>

          <!-- Search filter -->
          <div class="col-sm-12">
            <input
              type="search"
              class="form-control"
              placeholder="Search..."
              v-model="searchText"
              aria-label="Search"
            />
          </div>

          <!-- List -->
          <div class="col-sm-12">
            <div class="list-container" ref="list">
              <h4 class="text-center text-muted" v-if="filteredResults === null && !isLoading">
                Search for {{ typeName }}s to add...
              </h4>
              <h4 class="text-center" v-else-if="filteredResults === null && isLoading">
                Searching...
              </h4>
              <div v-if="filteredResults && !isLoading && filteredResults.length == 0">
                <h4 class="text-muted text-center">No matching results.</h4>
              </div>
              <div
                v-for="result in filteredResults"
                :key="result.id"
                class="group-list-item"
                :class="{ selected: result === selectedGroup }"
                @click="selectGroup(result)"
                @keydown.enter="selectGroup(result)"
              >
                <span class="name">{{ result.name }}</span
                ><br />
                <span
                  v-if="result.organisation"
                  class="organisation label"
                  :class="result === selectedGroup ? 'label-primary' : 'label-default'"
                  >{{ result.organisation.name }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row" v-if="errors && errors.length">
        <br />
        <div class="col-sm-12">
          <p class="col-sm-12 text-danger text-left" v-for="error in errors" v-bind:key="error">
            {{ error }}
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div>
        <span
          v-if="addedGroups.length > 0"
          class="text-success text-big"
          style="margin-right: 10px"
        >
          <span class="glyphicon glyphicon-ok"></span> Added {{ addedGroups.length }} {{ typeName
          }}{{ addedGroups.length > 1 ? "s" : "" }}
        </span>
        <button
          class="btn btn-primary btn-lg"
          @click="doMove()"
          :disabled="!selectedGroup || isLoading || isSaving"
          type="button"
        >
          {{ isSaving ? "Saving..." : "Add" }}
        </button>

        <button class="btn btn-default btn-lg" @click="cancel()" type="button">Close</button>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import axios from "axios";
import { Modal } from "uiv";
import TeamsOrGroups from "../../../typings/enums/teams-or-groups";
import {
  GroupDescriptionApiModel,
  OrganisationAndMembersApiModel,
  OrganisationGroupUpdateApiModel,
} from "../../../api-models";
import { AdminSearchResults } from "../../../views/admin/admin-search-page.vue";

let resolveCallback: ((value: boolean) => void) | null;
let rejectCallback: ((reason?: any) => void) | null;

const errors = ref<string[]>([]);
const organisationAndMembers = ref<OrganisationAndMembersApiModel | null>(null);
const teamsOrGroups = ref<TeamsOrGroups | null>(null);
const selectedGroup = ref<GroupDescriptionApiModel | null>(null);
const searchResults = ref<GroupDescriptionApiModel[] | null>(null);
const addedGroups = ref<GroupDescriptionApiModel[]>([]);
const searchText = ref("");
const isLoading = ref(false);
const isSaving = ref(false);
const shown = ref(false);
const dialogTitle = ref("");
let serverCallTimeout: any = null;

const existingGroupIds = computed(() => {
  if (teamsOrGroups.value === TeamsOrGroups.Teams) {
    return organisationAndMembers.value?.teams
      ?.map((m) => m.group.id)
      .concat(addedGroups.value.map((u) => u.id));
  } else if (teamsOrGroups.value === TeamsOrGroups.AnalysisGroups) {
    return organisationAndMembers.value?.analysisGroups
      ?.map((m) => m.group.id)
      .concat(addedGroups.value.map((u) => u.id));
  } else {
    return organisationAndMembers.value?.groups
      ?.map((m) => m.group.id)
      .concat(addedGroups.value.map((u) => u.id));
  }
});

const filteredResults = computed(() => {
  if (!searchResults.value) {
    return null;
  }
  return searchResults.value.filter(
    (g) => !existingGroupIds.value?.includes(g.id) && !g.organisation,
  );
});

const typeName = computed(() => {
  if (teamsOrGroups.value === TeamsOrGroups.Teams) {
    return "team";
  } else if (teamsOrGroups.value === TeamsOrGroups.AnalysisGroups) {
    return "analysis group";
  } else {
    return "group";
  }
});

const close = () => {
  shown.value = false;
  if (resolveCallback) {
    resolveCallback(addedGroups.value.length > 0);
  }
};

const cancel = () => {
  close();
};

const open = (
  organisationAndMembersParam: OrganisationAndMembersApiModel,
  teamsOrGroupsParam: TeamsOrGroups,
): Promise<boolean> => {
  organisationAndMembers.value = organisationAndMembersParam;
  teamsOrGroups.value = teamsOrGroupsParam;
  selectedGroup.value = null;
  searchText.value = "";
  searchResults.value = null;
  isLoading.value = false;
  isSaving.value = false;
  serverCallTimeout = null;
  errors.value = [];
  addedGroups.value = [];
  shown.value = true;
  dialogTitle.value = `Find ${teamsOrGroupsParam === TeamsOrGroups.Teams ? "Team" : teamsOrGroupsParam === TeamsOrGroups.AnalysisGroups ? "Analysis Group" : "Group"}`;
  return new Promise((resolve, reject) => {
    resolveCallback = resolve;
    rejectCallback = reject;
  });
};

const performSearch = () => {
  if (!searchText.value || searchText.value.trim().length < 3) {
    searchResults.value = null;
    return;
  }

  if (serverCallTimeout) {
    clearTimeout(serverCallTimeout);
  }
  serverCallTimeout = setTimeout(() => {
    const query = searchText.value.trim();
    searchResults.value = [];
    selectedGroup.value = null;
    isLoading.value = true;
    serverCallTimeout = null;
    axios.get(`/admin/search/query?q=${query}`).then((resp) => {
      const searchResultsData = resp.data as AdminSearchResults;
      if (teamsOrGroups.value === TeamsOrGroups.Teams) {
        searchResults.value = searchResultsData?.Teams ?? null;
      } else if (teamsOrGroups.value === TeamsOrGroups.AnalysisGroups) {
        // Filter for analysis groups
        searchResults.value = (searchResultsData?.OtherGroups ?? []).filter(g => 
          g.groupType === 'AnalysisGroup') ?? null;
      } else {
        // Regular groups (exclude analysis groups)
        searchResults.value = (searchResultsData?.OtherGroups ?? []).filter(g => 
          g.groupType !== 'AnalysisGroup') ?? null;
      }
      isLoading.value = false;
    });
  }, 500);
};

watch(searchText, () => {
  performSearch();
});

const selectGroup = (selection: GroupDescriptionApiModel) => {
  selectedGroup.value = selection;
};

const doMove = () => {
  if (!selectedGroup.value || !organisationAndMembers.value) {
    return;
  }

  const data: OrganisationGroupUpdateApiModel = {
    groupId: selectedGroup.value.id,
    organisationId: organisationAndMembers.value.id,
  };
  isSaving.value = true;
  axios
    .post("/api/organisationgroup", data)
    .then((res) => {
      if (res.status !== 200) {
        errors.value = ["There was a problem adding the group, please try again."];
      } else {
        if (selectedGroup.value) {
          addedGroups.value.push(selectedGroup.value);
        }
        errors.value = [];
        selectedGroup.value = null;
      }
    })
    .catch((error) => {
      errors.value = ["There was a problem adding the group, please try again."];
      if (error?.response?.data?.length) {
        errors.value.push(error.response.data);
      }
    })
    .finally(() => {
      isSaving.value = false;
    });
};

async function show(organisationModel, groupType, title = null) {
  return open(organisationModel, groupType);
}

defineExpose({
  open,
  show,
  close,
});
</script>

<style scoped lang="less">
@import "../../../styles/bootstrap/variables.less";

.list-container {
  max-height: 200px;
  min-height: 200px;
  overflow-y: scroll;
  margin-top: 20px;
  border: 1px solid lightgray;

  .group-list-item {
    margin: 10px;
    cursor: pointer;
    clear: both;

    .organisation {
      display: inline-block;
      margin-bottom: 10px;
    }

    &.selected {
      color: @link-color;
      font-weight: bold;
    }
  }
}
</style>
